import type { CmdLogSubAction, LoggerApi, LogLevelValue } from '@shared/logging/types'
import globalEvents from '@shared/iframe-messages/global-events'
import { captureCallerStack, LogLevel, serializeError } from '@shared/logging/types'

function send(subAction: CmdLogSubAction, data: Record<string, unknown>, callerStack?: string) {
  globalEvents.emit('log', {
    subAction,
    data,
    source: { layer: 'host' as const },
    timestamp: Date.now(),
    callerStack,
  })
}

// Frames: caller → info/warn/error → logWithStack → send
const LOG_FRAMES_TO_SKIP = 3
// Frames: caller → captureException/captureMessage → send
const CAPTURE_FRAMES_TO_SKIP = 2

function log(level: LogLevelValue, message: string, data?: Record<string, unknown>) {
  send('log', { message, level, extra: data })
}

function logWithStack(level: LogLevelValue, message: string, data?: Record<string, unknown>) {
  send('log', { message, level, extra: data }, captureCallerStack(LOG_FRAMES_TO_SKIP))
}

export const hostLogger: LoggerApi = {
  debug: (message, data) => log(LogLevel.Debug, message, data),
  info: (message, data) => logWithStack(LogLevel.Info, message, data),
  warn: (message, data) => logWithStack(LogLevel.Warning, message, data),
  error: (message, data) => logWithStack(LogLevel.Error, message, data),

  captureException(error, extra) {
    send('captureException', { ...serializeError(error), extra }, captureCallerStack(CAPTURE_FRAMES_TO_SKIP))
  },

  captureMessage(message, level, extra) {
    send('captureMessage', { message, level: level ?? LogLevel.Info, extra }, captureCallerStack(CAPTURE_FRAMES_TO_SKIP))
  },

  addBreadcrumb(breadcrumb) {
    send('addBreadcrumb', { ...breadcrumb, timestamp: breadcrumb.timestamp ?? Date.now() })
  },

  setContext(name, data) {
    send('setContext', { name, data })
  },

  setTag(key, value) {
    send('setTag', { key, value })
  },
}
